import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, KeyRound, Eye, EyeOff, ArrowRight, Loader2, ArrowLeft,
  Mail, ShieldCheck, RotateCcw, Lock,
} from "lucide-react";
import BrandPanel from "@/components/auth/BrandPanel";
import webLogo from "@/assets/images/web_logo.png";
import Field from "@/components/auth/Field";
import Alert from "@/components/auth/Alert";
import OtpInput from "@/components/auth/OtpInput";

import { getStrength } from "@/lib/passwordStrength";

import appConfig from "@/config/appConfig";

const API_BASE = appConfig.apiBase;

const REAL_CLINIC_APP_URL = appConfig.dashboardUrl;

async function fetchJson(url, options = {}) {
  try {
    const res = await fetch(url, options);
    const text = await res.text();
    let data;

    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!res.ok) {
      return {
        ok: false,
        message: data?.message || res.statusText || `Request failed with status ${res.status}`,
        data,
      };
    }

    return { ok: true, data };
  } catch (error) {
    return {
      ok: false,
      message: error?.message || "Network request failed.",
      data: null,
    };
  }
}

async function apiPostJson(path, body) {
  return fetchJson(`${API_BASE}/${path}`, {
    method: "POST",
    credentials: 'include',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function apiPostQuery(path, params) {
  const query = new URLSearchParams(params).toString();
  return fetchJson(`${API_BASE}/${path}?${query}`, {
    method: "POST",
  });
}

async function apiLogin({ clinicCode, password }) {
  return apiPostJson("login", { clinicCode, password });
}

async function apiRequestForgotOtp({ email, clinicCode }) {
  return apiPostQuery("request-forgot-password-otp", { email, clinicCode });
}

async function apiVerifyForgotOtp({ email, otp }) {
  const res = await apiPostQuery("verify-forgot-password-otp", { email, otp });
  if (!res.ok) return { ok: false, message: res.message, valid: false };
  const success = res.data?.success ?? true;
  return { ok: true, valid: Boolean(success), message: res.data?.message };
}

async function apiResetPassword({ email, clinicCode, otp, newPassword }) {
  return apiPostQuery("reset-password", { email, clinicCode, otp, newPassword });
}

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

function formatMMSS(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

export default function ClinicSignin() {
  const navigate = useNavigate();

  // Sign-in state
  const [clinicCode, setClinicCode] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpStatus, setOtpStatus] = useState("idle");
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [showNewPw, setShowNewPw] = useState(false);

  // Load remembered clinic code
  useEffect(() => {
    const saved = localStorage.getItem("rememberedClinicCode");
    if (saved) {
      setClinicCode(saved);
      setRememberMe(true);
    }
  }, []);

  // OTP countdown
  useEffect(() => {
    if (!otpSent || otpTimer <= 0) return;
    const t = setInterval(() => setOtpTimer((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [otpSent, otpTimer]);

  const handleClinicCodeChange = (e) => setClinicCode(e.target.value.toUpperCase());
  const toggleRememberMe = () => setRememberMe((v) => !v);
  const toggleShowPw = () => setShowPw((v) => !v);

  const resetForgotFlow = () => {
    setForgotEmail("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setOtp(["", "", "", "", "", ""]);
    setOtpStatus("idle");
    setOtpSent(false);
    setOtpTimer(0);
    setError("");
    setSuccess("");
  };

  const login = async () => {
    setError(""); setSuccess("");
    if (!clinicCode.trim()) return setError("Please enter your clinic code.");
    if (!password) return setError("Please enter your password.");
    setLoading(true);
    // Auth login uses the configured API base from env/runtime config.
    const res = await apiLogin({ clinicCode: clinicCode.trim(), password });
    setLoading(false);
    if (!res.ok) return setError(res.message || "Unable to sign in.");
    // Session cookie is set by backend (HttpOnly). Do not persist session data in localStorage.
    if (rememberMe) localStorage.setItem("rememberedClinicCode", clinicCode.toUpperCase());
    else localStorage.removeItem("rememberedClinicCode");
    setSuccess("Signed in. Redirecting…");
    setTimeout(() => {
      // Always redirect to the real clinic admin app URL (avoid navigating within the auth SPA)
      window.location.replace(REAL_CLINIC_APP_URL);
    }, 350);
  };

  const requestForgotOtp = async () => {
    setError(""); setSuccess("");
    if (!clinicCode.trim()) return setError("Enter your clinic code first.");
    if (!/^\S+@\S+\.\S+$/.test(forgotEmail)) return setError("Enter a valid email address.");
    setLoading(true);
    const res = await apiRequestForgotOtp({ email: forgotEmail, clinicCode });
    setLoading(false);
    if (!res.ok) return setError(res.message);
    setOtpSent(true);
    setOtpTimer(300);
    setOtpStatus("idle");
    setSuccess(res.message);
    setOtp(["", "", "", "", "", ""]);
  };

  const validateOtpLive = async (arr) => {
    const s = arr.join("");
    if (s.length !== 6) { setOtpStatus("idle"); return; }
    setOtpStatus("pending");
    const res = await apiVerifyForgotOtp({ email: forgotEmail, otp: s });
    setOtpStatus(res.valid ? "valid" : "invalid");
  };

  const handleForgotOtpChange = (i, v) => {
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (next.join("").length === 6) validateOtpLive(next);
    else setOtpStatus("idle");
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "");
    if (text.length === 6) {
      e.preventDefault();
      const arr = text.split("");
      setOtp(arr);
      validateOtpLive(arr);
    }
  };

  const resetPassword = async () => {
    setError(""); setSuccess("");
    if (forgotNewPassword.length < 8) return setError("Password must be at least 8 characters.");
    if (forgotNewPassword !== forgotConfirmPassword) return setError("Passwords do not match.");
    if (otpStatus !== "valid") return setError("Please verify the OTP first.");
    setLoading(true);

    try {
      const res = await apiResetPassword({
        email: forgotEmail, clinicCode, otp: otp.join(""), newPassword: forgotNewPassword,
      });
      if (!res.ok) return setError(res.message || "Unable to reset password. Please try again.");
      setSuccess(res.message || "Your password has been reset. You can now sign in.");
      setTimeout(() => { setForgotMode(false); resetForgotFlow(); }, 1400);
    } catch (err) {
      setError(err?.message || "Password reset request failed. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  const strength = getStrength(forgotNewPassword);

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] bg-form-panel">
      {/* Left — Brand panel */}
      <div className="hidden lg:block">
        <BrandPanel signin={!forgotMode} forgot={forgotMode} />
      </div>

      {/* Mobile mini-brand */}
      <div className="relative flex items-center justify-between px-4 sm:px-6 py-2 sm:py-3 lg:hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,215,68,0.16),_transparent_36%),linear-gradient(180deg,_#020406_0%,_#0f172a_45%,_#081024_100%)] text-white">
        <div className="flex items-center gap-3">
          <div className="w-0 sm:w-2 md:w-4 lg:w-6" />
          <img src={webLogo} alt="CliniQueue" className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto -ml-2 sm:-ml-1 md:ml-0" />
          <div className="ml-3 mt-0.5 h-px w-16 sm:w-24 md:w-32 bg-gradient-to-r from-transparent via-[rgba(56,189,248,0.75)] to-transparent opacity-90" />
        </div>
        <div className="chip !py-1">{forgotMode ? "Recovery" : "Sign in"}</div>
      </div>

      {/* Right — Form panel */}
      <div className="relative flex items-center justify-center px-5 py-8 sm:px-8 md:px-12 lg:py-14">
        <div className="w-full max-w-[460px]">
          <AnimatePresence mode="wait">
            {!forgotMode ? (
              <motion.div key="signin" {...fadeUp} className="space-y-6">
                <div className="text-left">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(222,82%,46%)]">
                    Welcome back
                  </div>
                  <h2 className="font-display text-3xl leading-[1.1] text-[hsl(220,45%,12%)] sm:text-4xl">
                    Sign in to your <span className="italic text-[hsl(222,82%,46%)]">clinic workspace</span>.
                  </h2>
                  <p className="mt-2 text-sm text-[hsl(220,15%,46%)]">
                    Continue where you left off — appointments, records, staff & billing.
                  </p>
                </div>

                <Alert type="error" onClose={() => setError("")} testId="signin-error">{error}</Alert>
                <Alert type="success" onClose={() => setSuccess("")} testId="signin-success">{success}</Alert>

                <div className="space-y-4">
                  <Field
                    id="clinicCode"
                    label="Clinic Code"
                    icon={Building2}
                    value={clinicCode}
                    onChange={handleClinicCodeChange}
                    placeholder="e.g. CLNPY12345"
                    autoComplete="username"
                    testId="clinic-code-input"
                  />
                  <Field
                    id="password"
                    label="Password"
                    icon={KeyRound}
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    testId="password-input"
                    suffix={
                      <button
                        type="button"
                        onClick={toggleShowPw}
                        className="rounded-md p-1.5 text-[hsl(220,15%,46%)] hover:text-[hsl(220,45%,12%)]"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        data-testid="toggle-password-visibility"
                      >
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[hsl(220,30%,28%)]">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={toggleRememberMe}
                      className="h-4 w-4 rounded border-[hsl(220,22%,78%)] text-[hsl(222,82%,46%)] focus:ring-[hsl(222,82%,46%)]"
                      data-testid="remember-me-checkbox"
                    />
                    Remember my clinic code
                  </label>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(true); setError(""); setSuccess(""); }}
                    className="text-sm font-semibold text-[hsl(222,82%,46%)] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="button"
                  onClick={login}
                  disabled={loading}
                  className="btn-primary w-full"
                  data-testid="signin-submit-btn"
                >
                  {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</>) : (<>Sign In <ArrowRight className="h-4 w-4" /></>)}
                </button>

                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t hairline" /></div>
                  <div className="relative flex justify-center"><span className="bg-[hsl(220,33%,98%)] px-3 text-[11px] uppercase tracking-[0.22em] text-[hsl(220,15%,46%)]">New here</span></div>
                </div>

                <div className="rounded-2xl border hairline bg-white/60 p-4 backdrop-blur-sm text-center">
                  <div className="text-sm text-[hsl(220,30%,28%)] leading-6">
                    Launch your clinic on CliniQueue.
                  </div>
                  <div className="mt-3">
                    <Link
                      to="/signup"
                      className="inline-flex items-center justify-center gap-1.5 font-semibold text-[hsl(222,82%,46%)] hover:underline"
                      data-testid="go-to-signup-link"
                    >
                      Start onboarding <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                <p className="text-center text-[11px] leading-relaxed text-[hsl(220,15%,46%)]">
                  Secure clinic sign-in using your registered clinic code and password.
                </p>
              </motion.div>
            ) : (
              <motion.div key="forgot" {...fadeUp} className="space-y-6">
                <button
                  onClick={() => { setForgotMode(false); resetForgotFlow(); }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-[hsl(220,15%,46%)] hover:text-[hsl(220,45%,12%)]"
                  data-testid="back-to-signin-link"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </button>

                <div>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(222,82%,46%)]">
                    Password recovery
                  </div>
                  <h2 className="font-display text-3xl leading-[1.1] text-[hsl(220,45%,12%)] sm:text-4xl">
                    {!otpSent ? (<>Reset your <span className="italic text-[hsl(222,82%,46%)]">password</span>.</>) : (<>Enter your <span className="italic text-[hsl(222,82%,46%)]">code</span>.</>)}
                  </h2>
                  <p className="mt-2 text-sm text-[hsl(220,15%,46%)]">
                    {!otpSent
                      ? "Verify your identity to receive a secure OTP by email."
                      : `We sent a 6-digit code to ${forgotEmail}.`}
                  </p>
                </div>

                <Alert type="error" onClose={() => setError("")}>{error}</Alert>
                <Alert type="success" onClose={() => setSuccess("")}>{success}</Alert>

                {!otpSent ? (
                  <div className="space-y-4">
                    <Field id="fc" label="Clinic Code" icon={Building2}
                      value={clinicCode} onChange={handleClinicCodeChange}
                      placeholder="e.g. CLNPY12345" testId="forgot-clinic-code" />
                    <Field id="fe" label="Registered Email" icon={Mail}
                      type="email" value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@clinic.com" testId="forgot-email" />
                    <button onClick={requestForgotOtp} disabled={loading}
                      className="btn-primary w-full" data-testid="send-otp-btn">
                      {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending code…</>) : (<>Send verification code <ArrowRight className="h-4 w-4" /></>)}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div>
                      <label className="mb-1.5 flex items-center justify-between text-[12px] font-semibold uppercase tracking-[0.14em] text-[hsl(220,30%,28%)]">
                        <span>Verification Code</span>
                        {otpTimer > 0 && (
                          <span className="font-mono-tab text-[11px] font-medium normal-case tracking-normal text-[hsl(220,15%,46%)]">
                            Expires in {formatMMSS(otpTimer)}
                          </span>
                        )}
                      </label>
                      <OtpInput
                        value={otp}
                        onChange={handleForgotOtpChange}
                        onPaste={handlePaste}
                        status={otpStatus}
                        testIdPrefix="forgot-otp"
                      />
                      <div className="mt-2 flex items-center justify-between">
                        <button
                          onClick={requestForgotOtp}
                          disabled={otpTimer > 240 || loading}
                          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[hsl(222,82%,46%)] disabled:opacity-40"
                          data-testid="resend-otp-btn"
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Resend code
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      <Field id="np" label="New Password" icon={Lock}
                        type={showNewPw ? "text" : "password"}
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        testId="new-password"
                        suffix={
                          <button type="button" onClick={() => setShowNewPw(v => !v)}
                            className="rounded-md p-1.5 text-[hsl(220,15%,46%)] hover:text-[hsl(220,45%,12%)]">
                            {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        }
                      />
                      {forgotNewPassword && (
                        <div>
                          <div className="strength-bar">
                            <div className="strength-fill" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                          </div>
                          <div className="mt-1 text-[11px] text-[hsl(220,15%,46%)]">Strength: <span className="font-semibold" style={{ color: strength.color }}>{strength.label}</span></div>
                        </div>
                      )}
                      <Field id="cp" label="Confirm Password" icon={Lock}
                        type={showNewPw ? "text" : "password"}
                        value={forgotConfirmPassword}
                        onChange={(e) => setForgotConfirmPassword(e.target.value)}
                        placeholder="Re-enter your new password"
                        testId="confirm-password"
                      />
                    </div>

                    <button onClick={resetPassword} disabled={loading}
                      className="btn-primary w-full" data-testid="reset-password-btn">
                      {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Resetting…</>) : (<>Reset password <ArrowRight className="h-4 w-4" /></>)}
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
